import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import { USER_SETTINGS_KEY } from "core/local_storage_utils";
import { parseJsonString } from "core/str_utils";

import {
	applyLsrTableDataFilter,
	applyLsrTableDataSelect,
	applyMultiLsrTableDataSelect,
	applyMultiVorTableDataSelect,
	applyVorTableDataFilter,
	applyVorTableDataSelect,
	fetchLsrTableData,
	fetchLsrVorForConnection,
	fetchVorTableData,
	manageLsrVorItemConnection,
	saveLsrXmlWithVorConnection,
} from "./services";

const initialState = () => {
	const userSettings = parseJsonString(localStorage.getItem(USER_SETTINGS_KEY));
	return {
		fetchStatus: null,
		fetchStatusText: null,
		lsrVorLeftWidth: userSettings?.lsrVor?.lsrVorLeftWidth ?? "calc(50% - 25px)",
		lsrs: [],
		lsrTableData: [],
		filtredLsrTableData: [],
		initSelectedLsr: {},
		selectedLsr: {},
		lsrViewModes: [
			{ id: 1, name: "Все позиции", attr: "ALL" },
			{ id: 2, name: "Только свободные", attr: "ONLY_FREE" },
			{ id: 3, name: "Только привязанные", attr: "ONLY_CONNECTED" },
			{ id: 4, name: "Только имеющие связь с выделенной позиций ВОР", attr: "CONNECTED_WITH_SELECTED_VOR_ITEM" },
		],
		selectedLsrViewMode: { id: 1, name: "Все позиции", attr: "ALL" },
		selectedLsrItem: null,
		multiSelectedLsrItems: [],
		vors: [],
		vorTableData: [],
		filtredVorTableData: [],
		initSelectedVor: {},
		selectedVor: {},
		vorViewModes: [
			{ id: 1, name: "Все позиции", attr: "ALL" },
			{ id: 2, name: "Только свободные", attr: "ONLY_FREE" },
			{ id: 3, name: "Только привязанные", attr: "ONLY_CONNECTED" },
			{ id: 4, name: "Привязанные к выделенной позиции ЛСР", attr: "CONNECTED_WITH_SELECTED_LSR_ITEM" },
		],
		selectedVorViewMode: { id: 1, name: "Все позиции", attr: "ALL" },
		selectedVorItem: null,
		multiSelectedVorItems: [],
		vorTableScrollTop: null,
		expandOnSelect: {
			columnName: "name",
			fontSize: "12px",
			safeSpace: 100,
			lsrTableClass: "lsr-table",
			vorTableClass: "vor-table",
		},
		displayOptions: {
			showVorTableResource: userSettings?.lsrVor?.showVorTableResource ?? false,
			vorTableRowHeight: 22,
			pageHeightWithoutTable: 215,
			tableHeadereHeight: 40,
			tableRowsCountBeforeScroll: 4,
		},
		modalVisible: { deleteConfirm: false },
	};
};

export const lsrVorSlice = createSlice({
	name: "lsrVor",
	initialState,
	reducers: {
		setFetchStatus: (state, { payload }) => {
			state.fetchStatus = payload;
		},
		setLeftWidth: (state, { payload }) => {
			state.lsrVorLeftWidth = payload;
		},
		setSelectedLsr: (state, { payload }) => {
			state.selectedLsr = payload;
		},
		setSelectedLsrViewMode: (state, { payload }) => {
			state.selectedLsrViewMode = payload;
			if (payload?.attr === "CONNECTED_WITH_SELECTED_VOR_ITEM") {
				if (state.selectedVorViewMode.attr === "CONNECTED_WITH_SELECTED_LSR_ITEM") {
					state.selectedVorViewMode = state.vorViewModes[0];
				}
			}
		},
		setSelectedVor: (state, { payload }) => {
			state.selectedVor = payload;
		},
		setSelectedVorViewMode: (state, { payload }) => {
			state.selectedVorViewMode = payload;
			if (payload?.attr === "CONNECTED_WITH_SELECTED_LSR_ITEM") {
				if (state.selectedLsrViewMode.attr === "CONNECTED_WITH_SELECTED_VOR_ITEM") {
					state.selectedLsrViewMode = state.lsrViewModes[0];
				}
			}
		},
		setLsrTableData: (state, { payload }) => {
			state.lsrTableData = payload;
		},
		setFiltredLsrTableData: (state, { payload }) => {
			state.filtredLsrTableData = payload;
		},
		setVorTableData: (state, { payload }) => {
			state.vorTableData = payload;
		},
		setFiltredVorTableData: (state, { payload }) => {
			state.filtredVorTableData = payload;
		},
		setInitSelectedLsr: (state, { payload }) => {
			state.initSelectedLsr = payload;
		},
		setInitSelectedVor: (state, { payload }) => {
			state.initSelectedVor = payload;
		},
		setSelectedLsrItem: (state, { payload }) => {
			state.selectedLsrItem = payload;
		},
		setSelectedVorItem: (state, { payload }) => {
			state.selectedVorItem = payload;
		},
		selectLsrItem: (state, { payload }) => {
			const { clickedRow, action } = payload;
			if (clickedRow?.model_name === "LocalEstimatesItems") {
				state.selectedLsrItem = clickedRow;
				applyLsrTableDataSelect({ state, action });
			} else if (clickedRow?.model_name === "LocalEstimatesSections") {
				state.filtredLsrTableData = state.filtredLsrTableData.map((section) => {
					if (section?.key === clickedRow?.key) {
						const visibleValue = !section.childs_visible;
						section.childs_visible = visibleValue;
						section._children = section?._children.map((item) => {
							item.visible = visibleValue;
							return item;
						});
					}
					return section;
				});
			}
		},
		multiSelectLsrItem: (state, { payload }) => {
			const { clickedRow, event } = payload;
			if (clickedRow?.model_name !== "LocalEstimatesItems") return;
			if (state.multiSelectedVorItems?.length > 0) {
				state.multiSelectedVorItems = [];
				applyVorTableDataSelect({ state });
			}
			applyMultiLsrTableDataSelect({ state, clickedRow, event });
		},
		selectVorItem: (state, { payload }) => {
			const { clickedRow, action } = payload;
			if (clickedRow?.model_name === "VorWork") {
				state.selectedVorItem = clickedRow;
				applyVorTableDataSelect({ state, action });
			} else if (clickedRow?.model_name === "VorSection") {
				state.filtredVorTableData = state.filtredVorTableData.map((section) => {
					if (section?.key === clickedRow?.key) {
						const visibleValue = !section.childs_visible;
						section.childs_visible = visibleValue;
						section._children = section?._children.map((item) => {
							item.visible = visibleValue;
							return item;
						});
					}
					return section;
				});
			}
		},
		multiSelectVorItem: (state, { payload }) => {
			const { clickedRow, event } = payload;
			if (clickedRow?.model_name !== "VorWork") return;
			if (state.multiSelectedLsrItems?.length > 0) {
				state.multiSelectedLsrItems = [];
				applyLsrTableDataSelect({ state, action: "click" });
			}
			applyMultiVorTableDataSelect({ state, clickedRow, event });
		},
		filterLsrTableData: (state) => {
			applyLsrTableDataFilter({ state });
		},
		filterVorTableData: (state) => {
			applyVorTableDataFilter({ state });
		},
		showLsrItemConnectedVorItems: (state, { payload }) => {
			const { vorFileName } = payload;
			const targetVor = state.vors.find((x) => x.file_name === vorFileName);
			if (!targetVor) return;
			state.selectedVor = targetVor;
			state.selectedVorViewMode = state.vorViewModes.find((mode) => mode.attr === "CONNECTED_WITH_SELECTED_LSR_ITEM");
		},
		showVorItemConnectedLsrItems: (state, { payload }) => {
			const { lsrId } = payload;
			const targetLsr = state.lsrs.find((x) => x.id === lsrId);
			if (!targetLsr) return;
			state.selectedLsr = targetLsr;
			state.selectedLsrViewMode = state.lsrViewModes.find((mode) => mode.attr === "CONNECTED_WITH_SELECTED_VOR_ITEM");
		},
		setSetting: (state, { payload }) => {
			const { group, name, value } = payload;
			if (group === "displayOptions" && name === "showVorTableResource") {
				state.displayOptions.showVorTableResource = value;
			}
		},
		setVorTableScrollTop: (state, { payload }) => {
			state.vorTableScrollTop = payload;
		},
		setModalVisible: (state, { payload }) => {
			state.modalVisible = { ...state.modalVisible, ...payload };
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchLsrVorForConnection.fulfilled, (state, { payload }) => {
			state.lsrs = payload.lsrs;
			const initLsr = payload.lsrs?.find((x) => state.initSelectedLsr?.id && String(x.id) === state.initSelectedLsr.id);
			if (initLsr?.id) {
				state.selectedLsr = initLsr;
				state.initSelectedLsr = {};
			} else {
				state.selectedLsr = payload.lsrs?.[0] || {};
			}

			state.vors = payload.vors;
			const initVor = payload.vors?.find((x) => state.initSelectedVor?.id && String(x.id) === state.initSelectedVor.id);
			if (initVor?.id) {
				state.selectedVor = initVor;
				state.initSelectedVor = {};
			} else {
				state.selectedVor = payload.vors?.[0] || {};
			}
			state.fetchStatus = "success";
		});
		builder.addCase(fetchLsrTableData.fulfilled, (state, { payload }) => {
			state.lsrTableData = payload;
			applyLsrTableDataFilter({ state });
			state.fetchStatus = "success";
		});
		builder.addCase(fetchVorTableData.fulfilled, (state, { payload }) => {
			state.vorTableData = payload;
			applyVorTableDataFilter({ state });
			state.fetchStatus = "success";
		});

		builder.addCase(manageLsrVorItemConnection.fulfilled, (state, { payload }) => {
			state.lsrTableData = payload.lsr_table_data;
			state.vorTableData = payload.vor_table_data;
			state.selectedLsrItem = payload.lsr_item;
			state.selectedVorItem = null;
			state.multiSelectedLsrItems = [];
			state.multiSelectedVorItems = [];
			applyLsrTableDataFilter({ state });
			applyVorTableDataFilter({ state });
			applyLsrTableDataSelect({ state });
			state.fetchStatus = "success";
		});

		builder.addMatcher(
			isAnyOf(
				fetchLsrVorForConnection.pending,
				fetchLsrTableData.pending,
				fetchVorTableData.pending,
				manageLsrVorItemConnection.pending,
				saveLsrXmlWithVorConnection.pending
			),
			(state) => {
				state.fetchStatus = "loading";
			}
		);
		builder.addMatcher(
			isAnyOf(
				fetchLsrVorForConnection.rejected,
				fetchLsrTableData.rejected,
				fetchVorTableData.rejected,
				manageLsrVorItemConnection.rejected,
				saveLsrXmlWithVorConnection.rejected
			),
			(state, action) => {
				state.fetchStatus = "failed";
				const errorText = action?.payload?.response?.data?.text;
				if (errorText) state.fetchStatusText = String(errorText);
			}
		);

		builder.addMatcher(isAnyOf(saveLsrXmlWithVorConnection.fulfilled), (state) => {
			state.fetchStatus = "success";
		});
	},
});

export const { actions: lsrVorActions, reducer: lsrVorReducer } = lsrVorSlice;
